/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The one order of the comparison list. Extracted from the old cards comparer
 * on 2026-10-01, byte for byte in its logic, because the results list then had
 * two renderers behind a build-time flag (the cards and the rows, until the
 * cleanup of 2026-10-07 left the rows alone) and the ranking was the one thing
 * the two could never disagree on. Pure: no React, no i18n, no window — the
 * bake and the browser call the same function.
 *
 * Every rule below is a key of ONE lexicographic order (sorted column → tie
 * closer under the payout sort → commission → closer → name); a rule that
 * only applied "when the payouts are equal" under the other sorts would make
 * the comparator cyclic, and Array.sort gives an inconsistent comparator no
 * defined order at all. Pinned in src/lib/__tests__/comparerSort.test.ts and,
 * rendered, in the engine's own suite
 * (src/components/comparison/__tests__/ComparisonEngine.test.tsx).
 */

import type { EREProvider } from '../types';
import { effectiveCommissionPct, rowOfferPricing } from './offerPricing.js';

export type SortField = 'commission' | 'contractDuration' | 'earnings';
export type SortDirection = 'asc' | 'desc';

export function parseDurationMonths(duration: string | null): number {
  if (!duration) return Number.POSITIVE_INFINITY;
  const match = duration.match(/(\d+)/);
  if (match) return parseInt(match[1]) * 12;
  return 999;
}

// Aanbieders that close a tie (Jasper, 2026-09-30): at an equal net payout the
// offer is listed after the offers it ties with, instead of taking its
// alphabetical place among them. PowerFlow's 15 % is an introductory rate —
// its own site says 18 % after the introduction period (price check of
// 2026-09-23) — so at an equal payout the offers whose rate lasts come first.
// Matched on the name with case and punctuation folded ("PowerFlow",
// "Powerflow", "Power-Flow"), not on the id: the name is what the record
// carries identically on acc and prd. A renamed record ("PowerFlow B.V.")
// silently drops out of the rule — update the set. Never touches a row that
// does not tie: a closer that pays out more still ranks above.
const TIE_CLOSERS = new Set(['powerflow']);
export const closesTies = (p: EREProvider): boolean =>
  TIE_CLOSERS.has(p.name.toLowerCase().replace(/[^a-z0-9]/g, ''));

export interface CompareOptions {
  sortField: SortField;
  sortDirection: SortDirection;
  /**
   * The row's estimated net payout at the comparison's volume and ERE price,
   * or null when it cannot be estimated (unknown commission or service costs).
   * Supplied by the caller: the price and the volume are theirs.
   */
  earningsOf: (p: EREProvider) => number | null;
  /**
   * The percentage the commission key compares — the sorted column under the
   * commission sort and the tie-break under the others. Absent: the row's
   * `commissionFee`. The comparer passes commissionSortValue at its volume and
   * price, so an offer whose cost follows the volume ranks on what it costs
   * there; a flat row reads its `commissionFee` either way.
   */
  commissionOf?: (p: EREProvider) => number | null;
}

/**
 * The commission % a row is sorted by at a volume: for a row that carries a
 * pricing model (a staffel, a vastrecht per kWh, a fixed rate, a
 * subscription) its effective % there (src/lib/offerPricing.js), else — and
 * when there is no yield to divide by — its `commissionFee`. Also what the
 * tier word (Laag / Gemiddeld / Hoog), the "laagste commissie" crown and the
 * profile's bar read, so the four can never disagree: it takes any public row
 * that carries the two fields (a comparer row, a profile contract).
 */
export function commissionSortValue(
  p: Pick<EREProvider, 'pricing' | 'commissionFee'>,
  volume: number,
  erePrice: number
): number | null {
  if (!p.pricing) return p.commissionFee;
  return effectiveCommissionPct(rowOfferPricing(p), volume, erePrice) ?? p.commissionFee;
}

/** The comparator behind every rendered order of the list. */
export function compareProviders(
  a: EREProvider,
  b: EREProvider,
  { sortField, sortDirection, earningsOf, commissionOf }: CompareOptions
): number {
  // One value per row for the whole comparison, so the order stays a single
  // lexicographic one: the commission key reads the same number as the
  // sorted column and as the tie-break.
  const feeOf = commissionOf ?? ((p: EREProvider) => p.commissionFee);
  // Providers with unknown commission or duration always rank last, regardless of direction
  const aUnknown = a.commissionFee === null || a.contractDuration === null;
  const bUnknown = b.commissionFee === null || b.contractDuration === null;
  if (aUnknown !== bUnknown) return aUnknown ? 1 : -1;

  let cmp = 0;
  switch (sortField) {
    case 'commission':
      cmp = (feeOf(a) ?? 0) - (feeOf(b) ?? 0);
      break;
    case 'contractDuration':
      cmp = parseDurationMonths(a.contractDuration) - parseDurationMonths(b.contractDuration);
      break;
    case 'earnings': {
      const eA = earningsOf(a) ?? 0;
      const eB = earningsOf(b) ?? 0;
      cmp = eA - eB;
      break;
    }
  }
  const primary = sortDirection === 'asc' ? cmp : -cmp;
  if (primary !== 0) return primary;
  // A tie on the sorted column. Under the payout sort the tie IS an equal net
  // payout, and a tie closer yields its place right here — before the
  // commission tie-break, so a lower commission cannot lift it back above the
  // offers it ties with.
  const closing = Number(closesTies(a)) - Number(closesTies(b));
  if (sortField === 'earnings' && closing !== 0) return closing;
  // Tie-breaker: lowest commission first (only when not already sorting by commission)
  if (sortField !== 'commission') {
    const byFee = (feeOf(a) ?? Number.POSITIVE_INFINITY) - (feeOf(b) ?? Number.POSITIVE_INFINITY);
    if (byFee !== 0) return byFee;
  }
  // Under the other sorts the closer only gives up its place in the name
  // order: it never ranks below an offer with a lower commission.
  if (closing !== 0) return closing;
  // Last resort: the name. Equal net payout AND equal commission used to fall
  // through to the feed's file order — 7- and 8-way ties on prd (2026-09-23) —
  // so a provider's place inside a tie was unexplainable. Plain code-point
  // order on the lowercased name, not localeCompare: the bake and the browser
  // must sort identically, or the hydrated list disagrees with the baked one.
  const an = a.name.toLowerCase();
  const bn = b.name.toLowerCase();
  return an < bn ? -1 : an > bn ? 1 : 0;
}

/** A sorted copy of `rows` (the input is never mutated). */
export const sortProviders = (
  rows: readonly EREProvider[],
  options: CompareOptions
): EREProvider[] => [...rows].sort((a, b) => compareProviders(a, b, options));
